import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Aboutcards from '../../components/aboutCards'
import Button from '../../components/button'
import Input from '../../components/input'
import PasswordReset from '../../components/passwordReset'
import Text from '../../components/text'
import { useAuth } from '../../core/AuthContext'
import { AxiosContext } from '../../core/AxiosContext'
import { useGlobal, useGlobalDispatch } from '../../core/GlobalContext'
import { LOGIN_DUCK, REQUEST_STATUS, ROUTES, TOAST_DUCK } from '../../core/actions'
import * as styles from './Login.css'
import { loginAction, loginOTPAction, requestOTPAction, resendOTPAction } from './LoginDuck'

export const Login = () => {
  const { setAuthState } = useAuth()
  const { publicAxios } = useContext(AxiosContext)
  const { loginDispatch, toastDispatch } = useGlobalDispatch()
  const navigate = useNavigate()

  const {
    loginState: { status, message, otpMessage, otpBaseUrl, otpVerificationId, otpResendCount, otpStatus, otpState },
  } = useGlobal()

  const [pan, setPan] = useState('')
  const [mobile, setMobile] = useState('')
  const [isOTP, setIsOTP] = useState(true)
  const [password, setPassword] = useState('')
  const [rollingTextIndex, setRollingTextIndex] = useState(0)
  // Pause on Hover About Us
  const [overAboutus, setOverAboutus] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    setPassword('')
  }, [isOTP])

  useEffect(() => {
    // Allow no more than 3 tries.
    // LOGIN -> ENTER OTP -> REENTER OTP -> RESEND OTP -> RIGHT/WRONG -> END
    if (otpResendCount >= 3) {
      loginDispatch({ type: LOGIN_DUCK.EOL })
      setPan('')
      setIsOTP(true)
    }
    // Clear input right after resending otp is clicked
    if (otpState === LOGIN_DUCK.RESEND_OTP) {
      setPassword('')
    }
  }, [otpResendCount, otpState, loginDispatch])

  // Reset State after Login success
  useEffect(() => {
    if (status === REQUEST_STATUS.SUCCESS) {
      loginDispatch({ type: LOGIN_DUCK.EOL })
    }
  }, [status, loginDispatch])

  const handlePANChange = val => setPan(val.toUpperCase().trim())
  const handleMobileChange = val => setMobile(val.trim())
  const handlePasswordChange = val => setPassword(val)
  const handleisOTPChange = () => setIsOTP(isOTP => !isOTP)
  const handleRegisterClick = () => navigate(ROUTES.REGISTER)
  const togglePasswordVisibility = () => setShowPassword(!showPassword)
  const loginOTPActionLocal = () =>
    loginOTPAction({
      username: pan,
      password: password,
      otp_verification_id: otpVerificationId,
      baseUrl: otpBaseUrl,
    })(publicAxios, loginDispatch, setAuthState)

  // Similar to the logic of Text in Login Button below
  const handleLogin = useCallback(() => {
    isOTP
      ? otpVerificationId
        ? // When 1st time OTP is generated and allow user to enter and re-enter OTP
          otpResendCount == 2
          ? // When Resend OTP is successful, allow login action. Else show Resend OTP Action
            otpState === LOGIN_DUCK.RESEND_OTP && otpStatus === REQUEST_STATUS.SUCCESS
            ? loginOTPActionLocal()
            : resendOTPAction({
                username: pan,
                otp_verification_id: otpVerificationId,
                baseUrl: otpBaseUrl,
              })(publicAxios, loginDispatch)
          : loginOTPActionLocal()
        : requestOTPAction({ username: pan, phone: mobile })(publicAxios, loginDispatch, setAuthState)
      : // When OTP is not selected do Login action with password
        loginAction({
          username: pan,
          password: password,
          otp_verification_id: otpVerificationId,
        })(publicAxios, loginDispatch, setAuthState)
  }, [
    isOTP,
    loginOTPActionLocal,
    otpBaseUrl,
    otpResendCount,
    otpState,
    otpStatus,
    otpVerificationId,
    pan,
    mobile,
    password,
  ])

  //  Password Forget Button
  const handlePasswordForget = () => {
    if (pan) {
      setShowResetPassword(true)
    } else {
      toastDispatch({
        type: TOAST_DUCK.SEND_TOAST,
        payload: {
          message: `Please Enter the PAN before resetting the password!`,
          type: REQUEST_STATUS.FAILURE,
        },
      })
    }
  }

  useEffect(() => {
    const handleKeyPress = event => {
      event.key === 'Enter' && handleLogin()
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [handleLogin, isOTP])

  // Rolling Text Timer
  useEffect(() => {
    // Mod 4 is the length of About Us Array
    if (!overAboutus) {
      const intervalID = setInterval(() => setRollingTextIndex(index => (index + 1) % 4), 3000)
      return () => clearTimeout(intervalID)
    }
  }, [overAboutus])

  return (
    <div className={styles.loginContent}>
      <div className={styles.cardWrapper}>
        {showResetPassword ? (
          // ##### Password Reset Page
          <PasswordReset
            pan={pan}
            phone={mobile}
            showResetPassword={showResetPassword}
            setShowResetPassword={setShowResetPassword}
          />
        ) : (
          // ######### Login Page
          <div className={styles.card}>
            <Text size='2' value='Welcome back, Investor!'></Text>
            <Text
              color='var(--reset-password-label-color)'
              className={styles.welcomeText}
              value='Login with your PAN (Permanent Account Number) '
            />
            <div className={styles.inputs}>
              <Input
                className={styles.input}
                placeholder='Enter your PAN'
                value={pan}
                onChange={handlePANChange}
                autoComplete='username'
              />
              {/* {otpMessage === 'Please enter registered mobile number to get OTP!' && ( */}
              <Input
                className={styles.input}
                placeholder='Enter your mobile number'
                value={mobile}
                onChange={handleMobileChange}
                autoComplete='tel-national'
              />
              {/* )} */}
              {isOTP ? (
                otpVerificationId ? (
                  otpResendCount == 2 ? (
                    otpState === LOGIN_DUCK.RESEND_OTP && otpStatus === REQUEST_STATUS.SUCCESS ? (
                      <Input
                        className={styles.input}
                        placeholder='Enter received OTP'
                        value={password}
                        onChange={handlePasswordChange}
                      />
                    ) : null
                  ) : (
                    <Input
                      className={styles.input}
                      placeholder='Enter received OTP'
                      value={password}
                      onChange={handlePasswordChange}
                    />
                  )
                ) : null
              ) : (
                <div className={styles.passwordInputWrapper}>
                  <Input
                    className={`${styles.input} ${styles.passwordInput}`}
                    placeholder='Enter your password'
                    value={password}
                    onChange={handlePasswordChange}
                    type={showPassword ? 'text' : 'password'}
                  />
                  <Button
                    className={styles.passwordBtn}
                    type='text'
                    label={showPassword ? 'Hide' : 'Show'}
                    handleClick={togglePasswordVisibility}
                  />
                </div>
              )}
              <Button
                label={isOTP ? 'Login using password' : 'Login using OTP'}
                type='signText'
                handleClick={handleisOTPChange}
              />
            </div>

            <div className={styles.errorMessage}>
              <Text size='0.85' color='var(--login-error-message-color)' bold>
                {otpMessage}
              </Text>
              <Text size='0.85' color='var(--login-error-message-color)' bold>
                {message}
              </Text>
            </div>

            <div className={styles.buttonsWrapper}>
              <Button
                label={
                  isOTP
                    ? otpVerificationId
                      ? otpResendCount === 2
                        ? otpState === LOGIN_DUCK.RESEND_OTP && otpStatus === REQUEST_STATUS.SUCCESS
                          ? 'Login'
                          : 'Resend OTP'
                        : 'Login'
                      : 'Send OTP'
                    : 'Login'
                }
                disabled={status === REQUEST_STATUS.PENDING}
                handleClick={handleLogin}
                type='signin'
              />
            </div>
            <div>
              <Text color='var(--reset-password-label-color)' size={0.9}>
                Forgot your password ?
                <Button label={'Click here'} type='signText' handleClick={handlePasswordForget} />
                to reset your password
              </Text>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
